// frontend/src/pages/DynamicLoginPage.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBranding } from '../core/branding/BrandingContext';
import { useAuth } from '../components/auth/SimpleAuthProvider';
import { LoadingScreen } from '../components/feedback/LoadingScreen';
import { getApiEndpoint } from '../core/config/apiConfig';

export const DynamicLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { branding, isLoading: brandingLoading, projectName } = useBranding();
  const { login, isLoading: authLoading } = useAuth();
  const [supportToastOpen, setSupportToastOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [allowedEmails, setAllowedEmails] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    const fetchAllowedEmails = async () => {
      try {
        const response = await fetch(
                    getApiEndpoint(`auth/allowed-emails/${projectName}`)
                  );
        const data = await response.json();
        setAllowedEmails(data.emails || []);
      } catch (err) {
        console.error('Failed to load allowed emails:', err);
      }
    };

    if (projectName) {
      fetchAllowedEmails();
    }
  }, [projectName]);


  // AUTO-DISMISS SUPPORT TOAST AFTER 6S
useEffect(() => {
  if (!supportToastOpen) return;
  const t = setTimeout(() => setSupportToastOpen(false), 6000);
  return () => clearTimeout(t);
}, [supportToastOpen]);


  if (brandingLoading) {
    return <LoadingScreen />;
  }

  if (!branding) {
    return <div>Error loading branding</div>;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const result = await login(email);
      if (result.success) {
        navigate('/dashboard');
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('Login failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEmailSelect = (selectedEmail: string) => {
    setEmail(selectedEmail);
    setShowDropdown(false);
  };

const normalizedInput = email.trim().toLowerCase();
const inputTokens = normalizedInput.split(/\s+/).filter(Boolean);
const filteredEmails =
  normalizedInput.length < 3 || inputTokens.length === 0
    ? []
    : allowedEmails.filter((e) => {
        const lower = e.toLowerCase();
        return inputTokens.every((t) => lower.includes(t));
      });


  return (
    <div 
      className="min-h-screen flex items-center justify-center px-4"
      style={{ backgroundColor: branding.backgroundColor }}
    >
      <div className="max-w-md w-full">
        {/* Microsoft-style Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center space-x-2 mb-4">
            {/* Microsoft-style squares logo */}
            <div className="grid grid-cols-2 gap-1 w-8 h-8">
              <div style={{ backgroundColor: '#f25022' }} className="w-3.5 h-3.5"></div>
              <div style={{ backgroundColor: '#7fba00' }} className="w-3.5 h-3.5"></div>
              <div style={{ backgroundColor: '#00a4ef' }} className="w-3.5 h-3.5"></div>
              <div style={{ backgroundColor: '#ffb900' }} className="w-3.5 h-3.5"></div>
            </div>
            {branding.logoUrl && (
              <img 
                src={branding.logoUrl} 
                alt={`${projectName} logo`}
                className="h-8 object-contain"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            )}
          </div>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-lg shadow-xl p-8">
          <h1 
            className="text-3xl font-bold text-center mb-2"
            style={{ color: branding.primaryColor }}
          >
            Sign in
          </h1>
          
          <p 
            className="text-center mb-8 text-sm"
            style={{ color: branding.secondaryColor }}
          >
            to continue to {branding.headingText}
          </p>

          <form onSubmit={handleSubmit}>
            <div className="mb-6 relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setShowDropdown(true);
                }}
                onFocus={() => setShowDropdown(true)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent"
               style={{ 
                  '--tw-ring-color': branding.primaryColor 
                } as React.CSSProperties}
                placeholder="Enter your email"
                required
                autoComplete="off"
              />
              
              {/* Dropdown for allowed emails */}
              {showDropdown && filteredEmails.length > 0 && email.trim().length >= 3 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {filteredEmails.map((allowedEmail, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleEmailSelect(allowedEmail)}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center space-x-2">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-semibold"
                          style={{ backgroundColor: branding.primaryColor }}
                        >
                          {allowedEmail.charAt(0).toUpperCase()}
                        </div>
                        <span className="text-sm">{allowedEmail}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting || authLoading}
              className="w-full py-3 rounded-lg font-semibold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90"
              style={{ backgroundColor: branding.primaryColor }}
            >
              {isSubmitting ? 'Signing in...' : 'Next'}
            </button>
          </form>

<div className="mt-6 text-center">
  <p className="text-xs text-gray-500">
    Can't access your account?{' '}
    <button
      type="button"
      onClick={() => setSupportToastOpen(true)}
      className="font-semibold cursor-pointer hover:underline"
      style={{ color: branding.secondaryColor }}
    >
      Contact for support
    </button>
  </p>
</div>
        </div>

        <div className="text-center mt-6">
          <button
            onClick={() => navigate('/')}
            className="text-sm hover:underline"
            style={{ color: branding.secondaryColor }}
          >
            ← Back to Home
          </button>
        </div>

        {/* Allowed Emails Info (dev only) */}
        {/* {process.env.NODE_ENV === 'development' && (
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-xs text-blue-800 font-semibold mb-1">Allowed Emails for {projectName}:</p>
            <div className="text-xs text-blue-600">
              {allowedEmails.map((e, i) => (
                <div key={i}>• {e}</div>
              ))}
            </div>
          </div>
        )}*/}
      </div>
{supportToastOpen && (
  <div className="fixed top-6 right-6 z-50">
    <div
      style={{ animation: 'toastIn .28s ease-out' }}
      className="w-110 bg-white border border-gray-200 rounded-xl shadow-2xl overflow-hidden ring-1 ring-gray-100"
    >
      <div className="p-3 flex items-center gap-3">
        <div className="flex-none">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-full flex items-center justify-center text-white shadow-md">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8m0 0v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" />
            </svg>
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm text-gray-900">Contact support</p>
          <p className="text-xs text-gray-600 truncate mt-0.5">Email <span className="font-mono text-blue-600">Israr.Ahmed@mu.ie</span> for help.</p>
        </div>

        <div className="flex items-center gap-2 ml-2">
          <button
            type="button"
            onClick={() => {
              const supportEmail = 'Israr.Ahmed@mu.ie';
              window.location.href = `mailto:${supportEmail}?subject=${encodeURIComponent('Support Request')}`;
              setSupportToastOpen(false);
            }}
            className="px-3 py-1 rounded-md bg-blue-600 text-white text-sm shadow-sm hover:bg-blue-700 transition"
          >
            Email
          </button>

          <button
            type="button"
            onClick={() => {
              const supportEmail = 'Israr.Ahmed@mu.ie';
              if (navigator.clipboard) navigator.clipboard.writeText(supportEmail);
              setSupportToastOpen(false);
            }}
            className="px-3 py-1 rounded-md bg-gray-50 text-gray-800 text-sm border hover:bg-gray-100 transition"
          >
            Copy
          </button>

          <button
            type="button"
            onClick={() => setSupportToastOpen(false)}
            className="ml-1 text-xs text-gray-500 hover:underline"
            aria-label="Close support toast"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
)}
    </div>
  );
};